import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Star, Zap } from "lucide-react";
import { usePublicData } from "../../context/PublicDataContext";

function splitIntoLines(text, n = 3) {
  const words = (text || "").trim().split(/\s+/);
  if (words.length <= 1) return [text];
  const lines = [];
  const per = Math.ceil(words.length / n);
  for (let i = 0; i < words.length; i += per) {
    lines.push(words.slice(i, i + per).join(" "));
  }
  return lines;
}

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.12 },
  }),
};

export const Hero = () => {
  const { content } = usePublicData();
  const hero = content.hero;
  const headline = splitIntoLines(hero.title, 3);
  const lastIdx = headline.length - 1;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden grain"
      data-testid="hero-section"
    >
      {/* background accents */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#0033FF]/10 blur-3xl" />
      <div className="absolute top-1/2 -right-40 w-[400px] h-[400px] rounded-full bg-[#0033FF]/8 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center relative z-10 w-full">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#0033FF]/15 bg-white px-4 py-1.5 text-xs font-semibold text-[#0033FF] mb-6"
            data-testid="hero-location-badge"
          >
            <MapPin className="w-3.5 h-3.5" />
            {hero.badge}
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tighter text-[#0b1221] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            {headline.map((line, i) => (
              <span className="reveal-mask" key={i}>
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {i === lastIdx ? (
                    <span className="text-[#0033FF]">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-6 max-w-xl text-lg text-[#5e6c84] leading-relaxed"
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#download"
              className="group inline-flex items-center gap-3 bg-[#0033FF] text-white text-base font-semibold px-8 py-4 rounded-full hover:bg-[#0026bf] hover:scale-[1.03] active:scale-95 transition-[transform,background-color] duration-200 shadow-[0_14px_40px_rgba(0,51,255,0.32)]"
              data-testid="hero-download-btn"
            >
              <Zap className="w-5 h-5" fill="currentColor" />
              {hero.primaryCta}
            </a>
            <a
              href="#services"
              className="inline-flex items-center gap-2 text-[#0b1221] font-semibold px-6 py-4 rounded-full border border-[#0b1221]/10 hover:border-[#0033FF]/40 hover:text-[#0033FF] transition-colors duration-200"
              data-testid="hero-explore-btn"
            >
              {hero.secondaryCta} <ArrowDown className="w-4 h-4" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            className="mt-10 flex items-center gap-6 text-sm text-[#5e6c84]"
          >
            <div className="flex items-center gap-1.5">
              <Star className="w-4 h-4 text-[#0033FF]" fill="currentColor" />
              <span className="font-semibold text-[#0b1221]">4.9</span> rated
            </div>
            <div className="h-4 w-px bg-[#0b1221]/10" />
            <div>
              <span className="font-semibold text-[#0b1221]">10k+</span>{" "}
              deliveries
            </div>
            <div className="h-4 w-px bg-[#0b1221]/10" />
            <div>
              <span className="font-semibold text-[#0b1221]">30 min</span> avg
            </div>
          </motion.div>
        </div>

        {/* Right: app screenshot in phone frame with parallax */}
        <div className="lg:col-span-5 relative flex justify-center lg:justify-end">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[360px] h-[360px] rounded-full bg-[#0033FF]/12 blur-3xl" />
          </div>

          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.9, rotate: 3 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div
              style={{ scale: scaleImg }}
              className="relative w-[280px] sm:w-[310px] rounded-[3rem] bg-[#0b1221] p-3 shadow-[0_45px_100px_rgba(0,51,255,0.3)] border border-white/10"
            >
              <div className="absolute left-1/2 -translate-x-1/2 top-3 w-32 h-6 bg-[#0b1221] rounded-b-2xl z-20" />
              <div className="relative rounded-[2.3rem] overflow-hidden bg-white">
                <img
                  src="/quickdrop-app.jpg"
                  alt="QuickDrop Go mobile app home screen"
                  className="w-full h-auto block"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </motion.div>

            <motion.div
              style={{ y: yBadge }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="absolute -left-6 bottom-20 glass rounded-2xl p-4 shadow-xl w-48"
              data-testid="hero-float-card"
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-[#0033FF] flex items-center justify-center text-white shrink-0">
                  <Zap className="w-5 h-5" fill="currentColor" />
                </span>
                <div>
                  <p className="text-xs text-[#5e6c84]">Arriving in</p>
                  <p className="font-display font-bold text-[#0b1221] text-lg">
                    12 minutes
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
